// BUILD-046 Owner direction of 2026-10-20 (finish-b, and the reconcile pass): TRUTHFUL ORDERING and a DUE-TODAY that is correct on the Phoenix calendar - the pure
// module behind the sortable column headings, the "Sort:" dropdown (Recommended included), the Overdue / Due today filters, the Overdue status choice and the
// Overdue wording of an unclaimed Shared task.
//
// It does NOT edit the legacy sort (sortModel.ts, whose five declarations are hashed by page-logic-regression and stay byte-identical). Those declarations remain
// the byte-identical REFERENCE for Oldest, Newest and Project, for the All / Urgent / High / No due date filters, and for the feature-off page. Every other
// ordering and date rule of the new views (Recommended, Priority, Task due date, Task, Assigned to, Status, the Overdue / Due today filters, the Overdue status
// count and the Overdue wording) is this module's. It answers what the legacy ones cannot:
//
//   RECOMMENDED  what the LEGACY Recommended does (sortModel.ts `sortWorkItems`, the `default:` branch - read from the code, not from its comment, which says
//              something else): every task gets a DATE BUCKET, in this order: (0) OVERDUE = has a due date, is not completed and the date is before today;
//              (1) DUE TODAY = the date is today (whatever the status); (2) HAS ANOTHER DATE = any other due date (later days, and a completed task whose date has
//              passed); (3) NO DATE. Buckets are listed in that order. Inside a bucket the tasks go by priority, highest first - Urgent 4, High 3, Medium 2,
//              Low 1 - and a task with NO priority counts as Low (a word that is not a priority counts as 0, below Low). Whatever still ties keeps the order it
//              arrived in (the legacy sort is a stable sort). The legacy decides "before today" and "today" with `new Date(dueDate)`, which for a date-only string
//              is UTC midnight - the previous evening in Phoenix - so it calls a task due TODAY overdue and a task due TOMORROW "due today" (SRN-10).
//              `recommendedOrder` below has the SAME buckets, the SAME priority order and the SAME ties; its ONE change is that the bucket comes from the
//              Phoenix calendar day (the helpers below, compared as YYYY-MM-DD strings): due today is bucket 1, due tomorrow is bucket 2. The only other
//              differences are on data the legacy never meets: a due date that is not a real YYYY-MM-DD day counts as no date, and a minimal (`redacted`) row
//              carries no date and no priority into the order (it is a no-date, no-priority row).
//
//   PRIORITY   highest first = Urgent, High, Medium, Low; lowest first = Low, Medium, High, Urgent. A task with NO priority (or a value that is not one of the four
//              stored priorities) is LAST in BOTH directions. (The legacy "priority" mode counts an unset priority as Low and has no reverse.)
//   DUE DATE   earliest first / latest first by CALENDAR date. A task without a usable date is LAST in BOTH directions. A task's Next follow-up date is a different
//              field and is never read here.
//   TASK       A-Z / Z-A on the title as written, case-insensitive, numbers in numeric order ("Task 2" before "Task 10"), accents ignored for the comparison
//              (Intl.Collator 'en', sensitivity 'base', numeric true). The title itself is never changed. An empty title is last in both directions.
//   ASSIGNED TO  A-Z / Z-A on the name the Team / Company row SHOWS (ownerName). A task nobody has taken yet ("Not yet taken") or without a name is LAST in both.
//   STATUS     ONE documented order: Active, Waiting, Blocked, Completed. Ascending = Active to Completed, descending = Completed to Active. An unknown status is LAST
//              in both. This is the order the server already uses (`STATUS_ORDER` = active 0, waiting 1, blocked 2, completed 3 in lib/lambda/work/task-service.ts:271).
//
// Ties keep the order the tasks came in (a stable sort with an explicit index tie-break). A task shown in its minimal form (`redacted`) shows no priority and no
// due date, so it carries neither into the order (sorting never reveals what the row hides).
//
// DUE TODAY. A date-only value such as "2026-10-20" is a calendar day, not an instant: `new Date('2026-10-20')` is UTC midnight, which is the previous evening in
// Phoenix (UTC-7 all year), so comparing it with local midnight calls a task due today overdue and one due tomorrow "due today" (SRN-10). Here the due date and the
// Phoenix day (`phoenixToday()` of dateLabels.ts, passed in) are compared as YYYY-MM-DD strings:
//   due == today -> "Today", not overdue;  due < today -> overdue (from the first moment of the next Phoenix day);  no / invalid date -> never overdue.
// A completed task is never overdue (the legacy rule kept). CHANGED 2026-10-23 (BUILD-046 G4, October 23 addendum, item 2): ONE function here reads followUpDate - `recommendedOrder`, through `followUpGroupOf`, to put a follow-up that is due now
// first. A follow-up date still NEVER makes a task overdue, due today or any other bucket: `recommendedBucket`, the due filters and the overdue rule do not read it.
//
// UNCLAIMED SHARED TASKS. The server sends an `overdue` flag only for a task somebody owns (an Open_Task); a task still waiting in the Shared queue has no owner,
// so it never carried the flag and read "Yesterday" or a date. Owner-approved 2026-10-20 (a DISPLAY rule only): a row whose recorded Task due date is before the
// Phoenix day, whose status is open (not completed), whose assignment has not been cancelled or ended and whose date is visible (not a minimal row) reads
// "Overdue · <date>" in the overdue tone - in Shared Tasks and on the "Not yet taken" rows of Team and Company. The server's flag, when it is present, still counts.
// Nothing about the data, the counts of open tasks (Open_Task_Count, the tab counts), permissions or visibility changes. A task due today reads "Today".
//
// ONE OVERDUE RULE (Owner direction of 2026-10-21, cycle A): "overdue" is decided in ONE place, `isOverdueTask` of overdueRule.ts, and every function below that says
// Overdue calls it - `taskIsOverdueOn`, `filterByDueOn('overdue')`, `recommendedBucket` (the Overdue bucket) and `rowIsOverdueOn` - exactly as the rows of every view, the
// Overdue count and the Status-menu Overdue choice do. A task is eligible when it is open (Active, Waiting or Blocked), its assignment is not cancelled or ended, it is not
// shown in its minimal form, and either its Task due date is before the Phoenix day or the server's overdue flag is set. The older text above describes the date test; the
// status, ended-assignment, minimal-row and server-flag parts are overdueRule.ts's.
//
// Pure module. Runtime imports: the date-only check of dateLabels.ts and the one overdue rule of overdueRule.ts (both pure). Erasable TypeScript only.

import { isIsoDate } from './dateLabels';
import { assignmentEnded, isOpenStatus, isOverdueOn, isOverdueTask } from './overdueRule';
import type { OverdueTask } from './overdueRule';

export type OrderKey = 'title' | 'priority' | 'due' | 'assignee' | 'status';
/** 'asc' / 'desc' are the DATA directions: Priority desc = highest first; Due asc = earliest; Task and Assigned to asc = A-Z; Status asc = Active to Completed. */
export type OrderDirection = 'asc' | 'desc';

/** The fields the ordering reads. Both the legacy WorkItem and the WorkTask of the new routes have them (all optional here). */
export interface OrderableTask {
  title?: unknown;
  priority?: unknown;
  dueDate?: unknown;
  status?: unknown;
  ownerName?: unknown;
  unclaimed?: unknown;
  redacted?: unknown;
  /** Read by Recommended only, to put a follow-up that is due now first (BUILD-046 G4). No other ordering reads it. */
  followUpDate?: unknown;
  lifecycle?: unknown;
  outcome?: unknown;
}

/** urgent 4 > high 3 > medium 2 > low 1: the four stored priorities. */
export const PRIORITY_RANK: Readonly<Record<string, number>> = Object.freeze({ urgent: 4, high: 3, medium: 2, low: 1 });
/** The ONE status order: Active, In progress, Waiting, Blocked, Completed (the server's STATUS_ORDER, lib/lambda/work/task-service.ts). In progress was added by BUILD-046 section C. */
export const STATUS_RANK: Readonly<Record<string, number>> = Object.freeze({ active: 0, in_progress: 1, waiting: 2, blocked: 3, completed: 4 });
/** The status order in words, for the Status heading's name and tooltip. */
export const STATUS_ORDER_TEXT = 'Active, In progress, Waiting, Blocked, Completed';

const COLLATOR = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

// ───────────────────────── the value each key sorts by (null = "has none": last in both directions) ─────────────────────────

/** The rank of `word` in `table`, or null when it is not one of the table's own keys (a value such as "constructor" is not a priority). */
function rankOf(table: Readonly<Record<string, number>>, word: string): number | null {
  const key = word.trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null;
}

function priorityValue(task: OrderableTask): number | null {
  if (task.redacted === true || typeof task.priority !== 'string') return null;
  return rankOf(PRIORITY_RANK, task.priority);
}

function dueValue(task: OrderableTask): string | null {
  if (task.redacted === true) return null;
  return isIsoDate(task.dueDate) ? task.dueDate : null;
}

function textValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function assigneeValue(task: OrderableTask): string | null {
  return task.unclaimed === true ? null : textValue(task.ownerName);
}

function statusValue(task: OrderableTask): number | null {
  return typeof task.status === 'string' ? rankOf(STATUS_RANK, task.status) : null;
}

interface Decorated<T, V> {
  item: T;
  index: number;
  value: V | null;
}

function orderBy<T, V>(items: readonly T[], valueOf: (item: T) => V | null, compare: (a: V, b: V) => number, direction: OrderDirection): T[] {
  const decorated: Decorated<T, V>[] = items.map((item, index) => ({ item, index, value: valueOf(item) }));
  decorated.sort((x, y) => {
    if (x.value === null && y.value === null) return x.index - y.index;
    if (x.value === null) return 1;
    if (y.value === null) return -1;
    const raw = compare(x.value, y.value);
    const signed = direction === 'asc' ? raw : -raw;
    return signed !== 0 ? signed : x.index - y.index;
  });
  return decorated.map((entry) => entry.item);
}

const byNumber = (a: number, b: number): number => a - b;
const byDate = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const byText = (a: string, b: string): number => COLLATOR.compare(a, b);

/** A new array: `items` ordered by `key` in `direction`. The input array is not changed. */
export function sortTasks<T extends OrderableTask>(items: readonly T[], key: OrderKey, direction: OrderDirection): T[] {
  switch (key) {
    case 'priority':
      return orderBy(items, priorityValue, byNumber, direction);
    case 'due':
      return orderBy(items, dueValue, byDate, direction);
    case 'title':
      return orderBy(items, (task: T) => textValue(task.title), byText, direction);
    case 'assignee':
      return orderBy(items, assigneeValue, byText, direction);
    case 'status':
      return orderBy(items, statusValue, byNumber, direction);
    default:
      return [...items];
  }
}

// ───────────────────────── due today / overdue on the Phoenix calendar ─────────────────────────

/** True when `dueDate` is the calendar day `todayIso` (both YYYY-MM-DD). Anything that is not a real date is false. */
export function isDueTodayOn(dueDate: unknown, todayIso: unknown): boolean {
  return isIsoDate(dueDate) && isIsoDate(todayIso) && dueDate === todayIso;
}

// `isOverdueOn` (a Task due date before a day) is the date test of THE rule and lives in overdueRule.ts; it is re-exported here so its callers and tests are unchanged.
export { isOverdueOn };

/** The fields the task-level rules read. `followUpDate` is deliberately absent: it never makes a task overdue. */
export type DueTask = OverdueTask;

/** Overdue as the page counts it: THE one rule (`isOverdueTask` of overdueRule.ts) - an open task whose assignment is live, shown in full, with a past Task due date or the server's flag. */
export function taskIsOverdueOn(task: DueTask, todayIso: unknown): boolean {
  return isOverdueTask(task, todayIso);
}

/** Due today as the page counts it: the Task due date is today's Phoenix day (as the legacy filter, whatever the status; the page filters by status first). */
export function taskIsDueTodayOn(task: DueTask, todayIso: unknown): boolean {
  return isDueTodayOn(task.dueDate, todayIso);
}

export type DueFilter = 'due-today' | 'overdue';

/** The "Due today" / "Overdue" quick filters over any tasks, with the rules above. A new array; incoming order kept. */
export function filterByDueOn<T extends DueTask>(items: readonly T[], filter: DueFilter, todayIso: unknown): T[] {
  return items.filter((task) => (filter === 'overdue' ? isOverdueTask(task, todayIso) : taskIsDueTodayOn(task, todayIso)));
}

// ───────────────────────── Recommended, on the Phoenix calendar ─────────────────────────

/** The date bucket of the legacy Recommended, in its order: 0 overdue, 1 due today, 2 any other date, 3 no date. */
export type RecommendedBucket = 0 | 1 | 2 | 3;

/**
 * The bucket of a task for Recommended. Same four buckets as the legacy sort; the difference is only how "overdue" and "today" are decided: the Task due date and
 * the Phoenix day `todayIso` (YYYY-MM-DD) are compared as calendar strings (`taskIsOverdueOn`, `taskIsDueTodayOn`). A date that is not a real YYYY-MM-DD day, and a
 * minimal (`redacted`) row, have no date: bucket 3. `followUpDate` is never read.
 */
export function recommendedBucket(task: OrderableTask, todayIso: unknown): RecommendedBucket {
  if (task.redacted === true) return 3;
  if (isOverdueTask(task, todayIso)) return 0;
  if (taskIsDueTodayOn(task, todayIso)) return 1;
  return isIsoDate(task.dueDate) ? 2 : 3;
}

/**
 * The priority rank Recommended sorts by inside a bucket, as the legacy does: Urgent 4, High 3, Medium 2, Low 1; no priority counts as Low (1); a word that is not
 * one of the four counts 0. A minimal row shows no priority, so it counts as having none.
 */
function recommendedPriorityRank(task: OrderableTask): number {
  if (task.redacted === true) return PRIORITY_RANK.low;
  const priority = task.priority;
  if (!priority) return PRIORITY_RANK.low;
  return typeof priority === 'string' && Object.prototype.hasOwnProperty.call(PRIORITY_RANK, priority) ? PRIORITY_RANK[priority] : 0;
}

/**
 * RECOMMENDED: overdue first, then due today, then the tasks with another date, then the tasks with none; inside each bucket the highest priority first; whatever
 * ties keeps its arrival order. `todayIso` is the Phoenix day (`phoenixToday()`). A new array; the input is not changed. See the header for the legacy rule this
 * repeats, and for its one change (the Phoenix calendar decides the bucket).
 */
export function recommendedOrder<T extends OrderableTask>(items: readonly T[], todayIso: unknown): T[] {
  const decorated = items.map((item, index) => ({ item, index, follow: followUpGroupOf(item, todayIso), bucket: recommendedBucket(item, todayIso), rank: recommendedPriorityRank(item) }));
  decorated.sort((x, y) => {
    // BUILD-046 G4 (October 23 addendum, item 2): the actionable follow-ups come first - missed ones (the earliest follow-up date first, then the highest priority), then those due today (the highest priority first) - and the
    // rest keeps the established order below. A follow-up never makes a task overdue and the deadline buckets are not changed.
    if (x.follow.group !== y.follow.group) return x.follow.group - y.follow.group;
    if (x.follow.group === 0) {
      if (x.follow.date !== y.follow.date) return x.follow.date < y.follow.date ? -1 : 1;
      return y.rank - x.rank || x.index - y.index;
    }
    if (x.follow.group === 1) return y.rank - x.rank || x.index - y.index;
    return x.bucket - y.bucket || y.rank - x.rank || x.index - y.index;
  });
  return decorated.map((entry) => entry.item);
}

/**
 * The follow-up group of a task for Recommended: 0 = a MISSED follow-up (the date is before today), 1 = a follow-up TODAY, 2 = neither. Only an unfinished task whose assignment is live and that is shown in full has one (the same eligibility
 * as the Needs attention order and the Follow-up due filter); a completed, ended or restricted task, and one with no usable follow-up date, is group 2. `date` is the follow-up date for group 0 and '' otherwise.
 */
export function followUpGroupOf(task: OrderableTask, todayIso: unknown): { group: 0 | 1 | 2; date: string } {
  if (task.redacted === true || !isOpenStatus(task.status) || assignmentEnded(task) || !isIsoDate(task.followUpDate) || !isIsoDate(todayIso)) return { group: 2, date: '' };
  if (task.followUpDate < todayIso) return { group: 0, date: task.followUpDate };
  return task.followUpDate === todayIso ? { group: 1, date: '' } : { group: 2, date: '' };
}

// ───────────────────────── the Overdue wording of a row (unclaimed Shared tasks included) ─────────────────────────

/** The fields the Overdue wording of a Shared / Team row reads. `followUpDate` is deliberately absent. */
export interface RowDueTask {
  status?: string;
  dueDate?: unknown;
  /** The server's flag: sent for a task somebody owns, never for an unclaimed one. */
  overdue?: unknown;
  lifecycle?: string;
  outcome?: unknown;
  unclaimed?: unknown;
  redacted?: unknown;
}

// (`isOpenStatus` and `assignmentEnded` are overdueRule.ts's now, imported above.)

/**
 * The Owner-approved display rule of 2026-10-20, kept exactly as it was: an UNCLAIMED, visible, open, not-ended task whose Task due date is before the Phoenix day. A task due
 * today or later, or without a date, is not. Since 2026-10-21 it is a SUBSET of `isOverdueTask` (which covers claimed tasks too and the server's flag): nothing calls it any
 * more but the tests that pin the 2026-10-20 behavior.
 */
export function unclaimedIsOverdueOn(task: RowDueTask, todayIso: unknown): boolean {
  return task.unclaimed === true && task.redacted !== true && isOpenStatus(task.status) && !assignmentEnded(task) && isOverdueOn(task.dueDate, todayIso);
}

/**
 * Whether a Shared or Team row reads "Overdue": THE one rule (`isOverdueTask`). Since 2026-10-21 this is no longer "the server's flag, or an unclaimed task past its date": a
 * claimed task past its Task due date reads Overdue too, whether or not the list was read before the day turned. A minimal row shows no date, so it never reads Overdue. This
 * changes how a row is written, never which tasks are open: Open_Task_Count and the tab counts are not computed from it.
 */
export function rowIsOverdueOn(task: RowDueTask, todayIso: unknown): boolean {
  return isOverdueTask(task, todayIso);
}
